import React, { useState, useEffect, useCallback } from 'react';
import PortaledModal from '../../components/Modals/PortaledModal';
import { clientConfig } from '../../config';
import { fetchIdVerificationWorkflow } from './paymentService';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import * as translator from '../../utils/translator';
import './IdVerificationModal.css';

const isNonProd = clientConfig.ENV === 'development' || clientConfig.ENV?.startsWith('stage');

const MAX_RETRY_COUNT = 2;

interface IdVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  hashID: string;
  language: string;
  paymentMethodName: string;
}

/** Replace {paymentMethodName} placeholder in a translated string */
const insertPaymentMethodName = (template: string, paymentMethodName: string): string => {
  return template?.replace('{paymentMethodName}', paymentMethodName) ?? '';
};

const IdVerificationModal: React.FC<IdVerificationModalProps> = ({ isOpen, onClose, onSubmit, hashID, language, paymentMethodName }) => {
  const [workflowSrc, setWorkflowSrc] = useState<string>('');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Translated strings
  const t = (key: string) => translator.translate(language?.toLowerCase(), 'payment', `idVerificationModal/${key}`) || '';
  const modalTitle = insertPaymentMethodName(t('title_compact'), paymentMethodName);
  const modalCancel = t('cancel');
  const modalLoading = t('loading');
  const modalError = t('error');
  const modalErrorFailed = t('errorFailed');
  const modalRetry = t('retry');

  // Fetch Jumio workflow when modal opens
  const fetchWorkflow = useCallback(() => {
    if (!hashID) return;

    setLoading(true);
    setError(null);
    setWorkflowSrc('');
    setIframeLoaded(false);

    fetchIdVerificationWorkflow(hashID, language)
      .then((data) => {
        if (data.alreadyVerified) {
          onSubmit();
        } else if (data.workflowHref) {
          setWorkflowSrc(data.workflowHref);
        } else {
          setError(modalError);
        }
      })
      .catch((err) => {
        window.clientLogger?.error?.('Error fetching ID verification workflow', err);
        setError(modalError);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [hashID, language, onSubmit]);

  // Fetch workflow when modal opens.
  useEffect(() => {
    if (isOpen) {
      MainTracker.track('custom', 'ID verification viewed');
      setRetryCount(0);
      fetchWorkflow();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setWorkflowSrc('');
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  // Stop the page behind the modal from scrolling while it is open
  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Listen for Jumio iframe postMessage events
  useEffect(() => {
    if (!isOpen) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = event.data && typeof event.data === 'string' ? JSON.parse(event.data) : null;

        if (data?.payload?.value === 'success') {
          MainTracker.track('custom', 'ID verification completed');
          onSubmit();
        }

        if (data?.payload?.value === 'error') {
          window.clientLogger?.error?.('Jumio iframe error in ID verification modal', data);
          if (retryCount < MAX_RETRY_COUNT) {
            setRetryCount((prev) => prev + 1);
            fetchWorkflow();
          } else {
            setError(modalErrorFailed);
          }
        }
      } catch (err) {
        window.clientLogger?.error?.('Error handling Jumio postMessage in ID verification modal', err);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen, retryCount, fetchWorkflow, onSubmit]);

  const handleRetry = () => {
    setRetryCount(0);
    fetchWorkflow();
  };

  return (
    <PortaledModal
      shouldShowModal={isOpen}
      shouldBackgroundClickHideModal={false}
      hideModal={onClose}
    >
      <div className="id-verification-modal-content">
        <div className="id-verification-modal-header">
          <h2 className="id-verification-modal-title">{modalTitle}</h2>
          <button className="id-verification-modal-close" onClick={onClose} aria-label="Close modal">
            &times;
          </button>
        </div>
        <div className="id-verification-modal-body">
          {loading && (
            <div className="id-verification-modal-loading">
              <div className="id-verification-modal-spinner" />
              <p className="id-verification-modal-text">{modalLoading}</p>
            </div>
          )}

          {error && !loading && (
            <div className="id-verification-modal-error-container">
              <p className="id-verification-modal-error">{error}</p>
              <button className="id-verification-modal-retry" onClick={handleRetry}>
                {modalRetry}
              </button>
            </div>
          )}

          {!loading && !error && workflowSrc && (
            <div className="id-verification-modal-jumio-container">
              <iframe
                className="id-verification-modal-jumio-iframe"
                src={workflowSrc}
                allow="camera;autoplay;fullscreen;clipboard-read;clipboard-write;accelerometer;gyroscope;magnetometer"
                allowFullScreen
                title="ID Verification"
                onLoad={() => setIframeLoaded(true)}
              />
              {/* Keep the spinner over the iframe until Jumio has loaded */}
              {!iframeLoaded && (
                <div className="id-verification-modal-loading id-verification-modal-loading-overlay">
                  <div className="id-verification-modal-spinner" />
                  <p className="id-verification-modal-text">{modalLoading}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="id-verification-modal-footer">
          {isNonProd && !loading && !error && workflowSrc && (
            <div className="id-verification-modal-dev-actions">
              <span className="id-verification-modal-dev-label">NON-PROD</span>
              <button
                className="id-verification-modal-dev-pass"
                onClick={onSubmit}
              >
                Submit Pending Verification
              </button>
            </div>
          )}
          <button className="id-verification-modal-cancel" onClick={onClose}>
            {modalCancel}
          </button>
        </div>
      </div>
    </PortaledModal>
  );
};

export default IdVerificationModal;
